import { Button, SegmentedButtonGroup, Stack, Text } from '@olytic/foundation';
import { CatalogGroups } from '@olytic/foundation/cpq';
import { useState } from 'react';

import { formatCurrency } from '../objects/format';
import { catalogGroups } from './orderView';
import type { BuilderLine, CatalogProduct } from './quotesApi';

const FAMILIES = [
  { value: 'consulting', label: 'Consulting' },
  { value: 'studio', label: 'Studio' },
  { value: 'software', label: 'Foundation' },
  { value: 'agents', label: 'AI Agents' },
] as const;
type Family = (typeof FAMILIES)[number]['value'];

const HINTS: Record<Family, string> = {
  consulting: 'Packaged engagements and ongoing delivery capacity.',
  studio: 'People-led work to design, build and launch the application. Foundation templates are assembled for you and carry no design or build fee.',
  software: "Foundation licenses and add-ons, priced by the customer's Company Size.",
  agents: "AI agents built on the customer's own systems, sold in phases: design, build, measured rollout and support.",
};

/**
 * Catalog: every product by family and group, with its quantity on this
 * quote. Setting a quantity adds the product, changes its line, or (at 0)
 * removes it.
 */
export function CatalogTab({
  catalog,
  lines,
  priceOf,
  busy,
  onQuantityChange,
}: {
  catalog: CatalogProduct[];
  lines: BuilderLine[];
  /** What the catalog shows for a product: its price, or null when it needs a Company Size. */
  priceOf: (product: CatalogProduct) => number | null;
  busy: boolean;
  onQuantityChange: (product: CatalogProduct, quantity: number) => void;
}): JSX.Element {
  const [family, setFamily] = useState<Family>('studio');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const groups = catalogGroups(catalog, lines, family, priceOf, formatCurrency);
  return (
    <Stack>
      <SegmentedButtonGroup label="Product Family" options={FAMILIES} value={family} onChange={(v) => setFamily(v)} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Text tone="muted">{HINTS[family]}</Text>
        <div className="flex gap-2">
          <Button variant="neutral" size="sm" onClick={() => setOpen(new Set(groups.map((g) => g.key)))}>Expand all</Button>
          <Button variant="neutral" size="sm" onClick={() => setOpen(new Set())}>Collapse all</Button>
        </div>
      </div>
      <CatalogGroups
        groups={groups}
        expanded={open}
        onExpandedChange={setOpen}
        disabled={busy}
        onQuantityChange={(item, quantity) => {
          const product = catalog.find((p) => p.id === item.id);
          if (product) onQuantityChange(product, quantity);
        }}
      />
    </Stack>
  );
}
